import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play, Sun, Moon, ChevronDown } from 'lucide-react';

const LandingPage = () => {
  const [theme, setTheme] = useState('dark');
  const [activeReel, setActiveReel] = useState('01');
  const [timecode, setTimecode] = useState('00:00:00:00');
  const [showMobileCTA, setShowMobileCTA] = useState(false);

  // Theme Management
  useEffect(() => {
    const savedTheme = localStorage.getItem('ioh-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    document.documentElement.setAttribute('data-theme', initialTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('ioh-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // Live Timecode
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const hours = String(Math.floor(elapsed / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
      const seconds = String(elapsed % 60).padStart(2, '0');
      const frames = String(Math.floor((Date.now() - startTime) % 1000 / 41.67)).padStart(2, '0');
      setTimecode(`${hours}:${minutes}:${seconds}:${frames}`);
    }, 41.67);
    return () => clearInterval(interval);
  }, []);

  // Section Observer
  useEffect(() => {
    const sections = document.querySelectorAll('[data-reel]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveReel(entry.target.getAttribute('data-reel'));
          }
        });
      },
      { threshold: 0.3 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Mobile Sticky CTA
  useEffect(() => {
    const handleScroll = () => setShowMobileCTA(window.scrollY > 800);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToReel = (reelId) => {
    const element = document.getElementById(`reel-${reelId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--background)', color: 'var(--text-primary)' }} className="min-h-screen font-sans">
      
      {/* TOP VIEWFINDER BAR */}
      <TopViewfinder timecode={timecode} activeReel={activeReel} theme={theme} toggleTheme={toggleTheme} scrollToReel={scrollToReel} />

      {/* REEL 01 - HERO */}
      <Reel01Hero scrollToReel={scrollToReel} />

      {/* REEL 01A - VIRAL FEED PROOF */}
      <Reel01AViralFeed />

      {/* REEL 02 - BRANDS */}
      <Reel02Brands />

      {/* REEL 03 - THE PROBLEM */}
      <Reel03Problem />

      {/* REEL 04 - WHAT WE DO */}
      <Reel04WhatWeDo scrollToReel={scrollToReel} />

      {/* REEL 05 - CASE STUDIES */}
      <Reel05CaseStudies />

      {/* REEL 06 - WHO WE WORK WITH */}
      <Reel06Industries />

      {/* REEL 07 - HOW IT WORKS */}
      <Reel07Process />

      {/* REEL 08 - WHY IOH */}
      <Reel08WhyIOH />

      {/* REEL 09 - FAQ */}
      <Reel09FAQ />

      {/* REEL 10 - BOOKING FUNNEL */}
      <Reel10BookingFunnel />

      {/* BOTTOM TIMELINE SCRUBBER */}
      <BottomTimeline activeReel={activeReel} scrollToReel={scrollToReel} />

      {/* MOBILE STICKY CTA */}
      <MobileStickyCTA show={showMobileCTA} scrollToReel={scrollToReel} />

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

// TOP VIEWFINDER BAR COMPONENT
const TopViewfinder = ({ timecode, activeReel, theme, toggleTheme, scrollToReel }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      style={{
        backgroundColor: scrolled ? 'var(--background)' : 'transparent',
        borderBottom: `2px solid var(--border)`,
      }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-opacity-95' : ''
      }`}
    >
      <div className="flex items-center justify-between px-4 py-3 text-xs md:text-sm font-mono">
        <div className="flex items-center gap-3 md:gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--accent-coral)' }}></div>
            <span style={{ color: 'var(--text-muted)' }} className="hidden sm:inline">ROLLING ●</span>
            <span style={{ color: 'var(--text-primary)' }} className="font-bold">{timecode}</span>
          </div>
          <span style={{ color: 'var(--text-muted)' }} className="hidden md:inline text-[10px] md:text-xs">
            24mm · LOG · 4K · 24FPS
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded border-2 transition-all"
            style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Primary CTA */}
          <button
            onClick={() => scrollToReel('10')}
            className="hidden md:flex px-4 py-2 font-bold text-sm transition-all shadow-brutal-hover border-2 rounded"
            style={{
              backgroundColor: 'var(--accent-volt)',
              borderColor: 'var(--border)',
              color: 'var(--background)',
            }}
          >
            Book Call
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// REEL 01 - HERO
const Reel01Hero = ({ scrollToReel }) => {
  return (
    <section id="reel-01" data-reel="01" className="relative pt-32 md:pt-40 pb-20 md:pb-32 px-4 md:px-6 min-h-screen flex items-center">
      <div className="max-w-7xl mx-auto w-full relative z-10">
        {/* Telemetry Tag */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6 font-mono text-xs md:text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          REEL 01 · MASTER CUT // 24mm · LOG · 4K · 24FPS
        </motion.div>

        {/* Kinetic Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6"
          style={{ fontFamily: '"Space Grotesk", sans-serif' }}
        >
          Stop Feeding The Scroll.{' '}
          <span
            className="inline-block px-3 py-1 border-2 rounded"
            style={{
              backgroundColor: 'var(--accent-volt)',
              borderColor: 'var(--border)',
              color: 'var(--background)',
            }}
          >
            Start Building Pipeline.
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-base md:text-lg max-w-3xl mb-8"
          style={{ color: 'var(--text-muted)' }}
        >
          IOH Media is a Canadian cinema production and performance growth agency. We study your business and market mathematics, engineer the strategy, and execute everything in-house—short-form content, paid ads, and high-converting web infrastructure.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <button
            onClick={() => scrollToReel('10')}
            className="px-8 py-4 font-bold text-base rounded border-2 shadow-brutal-hover transition-all flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'var(--accent-volt)',
              borderColor: 'var(--border)',
              color: 'var(--background)',
            }}
          >
            Book a Free Strategy Call
            <ArrowRight size={20} />
          </button>
          <button
            onClick={() => scrollToReel('01A')}
            className="px-8 py-4 font-semibold text-base rounded border-2 transition-all flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'var(--surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-primary)',
            }}
          >
            Watch Showreel
            <Play size={18} />
          </button>
        </motion.div>

        {/* Micro-Trust Strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="flex flex-wrap gap-4 text-xs md:text-sm font-mono"
          style={{ color: 'var(--text-muted)' }}
        >
          <span>✓ 1,980+ Conversions Engineered</span>
          <span>•</span>
          <span>✓ In-House Production, Ads & Web</span>
          <span>•</span>
          <span>✓ Flexible Growth Tiers</span>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// REUSABLE COMPONENTS
// ============================================

// Animated Counter Component
const CounterStat = ({ value, suffix = '', duration = 2000 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = typeof value === 'string' ? parseInt(value.replace(/,/g, '')) : value;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  return (
    <span ref={ref} className="font-black font-mono">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

// Floating Sticker Component
const FloatingSticker = ({ children, rotation = -2 }) => {
  return (
    <span
      className="inline-block px-3 py-1 border-2 rounded text-xs font-mono font-bold float-sticker"
      style={{
        backgroundColor: 'var(--accent-coral)',
        borderColor: 'var(--border)',
        color: 'var(--background)',
        transform: `rotate(${rotation}deg)`,
      }}
    >
      {children}
    </span>
  );
};

// ============================================
// REEL 01A - VIRAL FEED PROOF
// ============================================

const Reel01AViralFeed = () => {
  const reels = [
    {
      brand: 'Sipan Tires & Rims',
      handle: '@sipantires',
      logo: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/c78801e1430d6712_IMG_1139.webp',
      videoUrl: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/953cea0984dc520a_Sipan-Tires-and-Rims-fdown.net.mov',
      views: 20800000,
      likes: 59000,
      comments: 14000,
      shares: 4000,
      type: 'Organic Viral Cinema',
      callout: '+608,827% view explosion from organic short-form reels',
    },
    {
      brand: 'Talia Flowers',
      handle: '@taliaflowers',
      logo: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/55c61c3369249832_IMG_1201.jpeg',
      videoUrl: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/f862504d1a451685_SaveVid.Net_AQMiGi_aa4T7zqI0nXtmjkdnp8tWpkH3aytbfSZBApDIQ6KZ-Nf7FJGDfaspJTnm7Osg9DigV9dTz5_X83mms9uGN1meqz3FcSzjs1Y.mov',
      views: 1800000,
      likes: 29000,
      comments: 6000,
      shares: 1200,
      leads: 839,
      callout: '839 direct customer leads generated via Reels + Paid Ads',
    },
    {
      brand: 'RoyalBlendz Barbershop',
      handle: '@royalblendz',
      logo: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/29e589d449367c02_IMG_1205.jpeg',
      videoUrl: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/9faa9c6b94476601_SaveVid.Net_AQNMdjfz0JfAWqwu5_3KwR8xUAHaooe7JGL255aRV-D79XqbrWD6cAsCs6XutmqwRY7ZiN2SpQ4m1fmBzKC0cCiO99jQF1kTqsR1xYM.mov',
      views: 1500000,
      likes: 10000,
      comments: 2000,
      shares: 500,
      leads: 128,
      callout: '+15,828% view surge & 128 new local clients in the chair',
    },
    {
      brand: 'Premium AutoWorks',
      handle: '@premiumautoworks',
      logo: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/eb46735f6be8bcef_IMG_1203.png',
      videoUrl: 'https://customer-assets-agu9un31.emergentagent.net/job_ottawa-growth/artifacts/74de93d91ed40ed4_igexport-DaGumQQugkh.mov',
      views: 450000,
      likes: 2000,
      comments: 300,
      shares: 150,
      leads: 528,
      callout: '+2,648% view surge & 528 new customer bookings',
    },
  ];

  return (
    <section id="reel-01A" data-reel="01A" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)` }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 01A · THE FEED
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Content that travels way past your follower count.
          </h2>
          <p className="text-base md:text-lg" style={{ color: 'var(--text-muted)' }}>
            Real vertical cinema we shot and distributed for clients, with the exact numbers they pulled.
          </p>
        </motion.div>

        {/* Mobile: Snap-Scroll */}
        <div className="md:hidden overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scroll-smooth">
          <div className="flex gap-4" style={{ width: `${reels.length * 320}px` }}>
            {reels.map((reel, idx) => (
              <div key={idx} className="snap-start">
                <ViralCard reel={reel} index={idx} />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reels.map((reel, idx) => (
            <ViralCard key={idx} reel={reel} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ViralCard = ({ reel, index }) => {
  const [reactions, setReactions] = useState([]);
  const videoRef = useRef(null);

  const handleReaction = (emoji) => {
    const id = Date.now();
    setReactions([...reactions, { id, emoji }]);
    setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), 1000);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num;
  };

  // Force video to play when in viewport
  useEffect(() => {
    if (videoRef.current && reel.videoUrl) {
      const video = videoRef.current;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.play().catch(() => {
                // Autoplay blocked, will show controls instead
              });
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(video);
      return () => observer.disconnect();
    }
  }, [reel.videoUrl]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.03, rotateY: 2 }}
      className="relative w-[300px] md:w-auto p-4 rounded-lg border-2 transition-all"
      style={{
        backgroundColor: 'var(--surface)',
        borderColor: 'var(--border)',
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Brand Info with Logo */}
      <div className="flex items-center gap-2 mb-3">
        {reel.logo ? (
          <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'var(--surface)' }}>
            <img src={reel.logo} alt={reel.brand} className="w-full h-full object-contain p-1" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full" style={{ backgroundColor: 'var(--accent-cyan)' }}></div>
        )}
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{reel.brand}</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{reel.handle}</p>
        </div>
      </div>

      {/* Video or Mockup */}
      <div className="aspect-[9/16] rounded mb-4 relative overflow-hidden bg-black">
        {reel.videoUrl ? (
          <video
            ref={videoRef}
            src={reel.videoUrl}
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
            }}
            loop
            muted
            playsInline
            controls
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Play size={48} style={{ color: 'var(--text-muted)' }} />
          </div>
        )}
      </div>

      {/* Engagement Metrics */}
      <div className="space-y-2 mb-4">
        {reel.views && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="font-mono" style={{ color: 'var(--text-muted)' }}>Views</p>
              <p className="text-lg font-black font-mono" style={{ color: 'var(--accent-volt)' }}>
                {formatNumber(reel.views)}+
              </p>
            </div>
            {reel.likes && (
              <div>
                <p className="font-mono" style={{ color: 'var(--text-muted)' }}>Likes</p>
                <p className="text-lg font-black font-mono" style={{ color: 'var(--accent-volt)' }}>
                  {formatNumber(reel.likes)}+
                </p>
              </div>
            )}
          </div>
        )}
        {reel.comments && reel.shares && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="font-mono" style={{ color: 'var(--text-muted)' }}>Comments</p>
              <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
                {formatNumber(reel.comments)}+
              </p>
            </div>
            <div>
              <p className="font-mono" style={{ color: 'var(--text-muted)' }}>Shares</p>
              <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
                {formatNumber(reel.shares)}+
              </p>
            </div>
          </div>
        )}
        {reel.leads && (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {reel.leads} Inbound Leads
          </p>
        )}
        {reel.type && !reel.likes && (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {reel.type}
          </p>
        )}
      </div>

      {/* Callout */}
      <div className="p-3 rounded border mb-3" style={{ backgroundColor: 'var(--accent-volt)', borderColor: 'var(--border)', opacity: 0.1 }}>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--background)' }}>
          {reel.callout}
        </p>
      </div>

      {/* Reaction Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => handleReaction('🔥')}
          className="flex-1 py-2 rounded border-2 text-sm transition-all hover:scale-105"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
        >
          🔥
        </button>
        <button
          onClick={() => handleReaction('📈')}
          className="flex-1 py-2 rounded border-2 text-sm transition-all hover:scale-105"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
        >
          📈
        </button>
      </div>

      {/* Floating Reactions */}
      <AnimatePresence>
        {reactions.map((r) => (
          <motion.div
            key={r.id}
            initial={{ y: 0, opacity: 1, scale: 1 }}
            animate={{ y: -80, opacity: 0, scale: 1.5 }}
            exit={{ opacity: 0 }}
            className="absolute text-2xl pointer-events-none"
            style={{ left: '50%', bottom: '20%' }}
          >
            {r.emoji}
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================
// REEL 02 - BRANDS
// ============================================

const Reel02Brands = () => {
  const clients = [
    'Talia Flowers', 'Sipan Tires & Rims', 'Premium AutoWorks', 'RoyalBlendz Barbershop',
    'CarPro Auto Brokers', 'OnPoint Towing', 'Moe Style Barbershop', 'Orange Cafe',
    'Cornerstone Waste Solutions', 'AnyDrop', 'Anni\'s Café', 'E.D. Waste Management',
    'LHGS Contracting', 'South Keys Health Center', 'Aqua Detailing', 'BBQ House',
  ];

  // Split brands into two rows
  const topRow = clients.slice(0, 8);
  const bottomRow = clients.slice(8);

  return (
    <section id="reel-02" data-reel="02" className="py-12 md:py-16 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)`, backgroundColor: 'var(--surface)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 02 · PARTNER ROSTER
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Brands we scale with.
          </h2>
        </motion.div>

        <div className="space-y-4">
          {/* Top Row - Scrolling Left */}
          <div className="relative overflow-hidden py-2">
            <motion.div
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
              className="flex whitespace-nowrap"
            >
              {[...topRow, ...topRow].map((client, idx) => (
                <span
                  key={idx}
                  className="mx-3 px-4 py-2 rounded-full border-2 text-xs md:text-sm font-medium whitespace-nowrap"
                  style={{
                    backgroundColor: 'var(--background)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {client}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Bottom Row - Scrolling Right */}
          <div className="relative overflow-hidden py-2">
            <motion.div
              animate={{ x: ['-50%', '0%'] }}
              transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
              className="flex whitespace-nowrap"
            >
              {[...bottomRow, ...bottomRow].map((client, idx) => (
                <span
                  key={idx}
                  className="mx-3 px-4 py-2 rounded-full border-2 text-xs md:text-sm font-medium whitespace-nowrap"
                  style={{
                    backgroundColor: 'var(--background)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {client}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        <p className="text-center text-xs md:text-sm mt-6 font-mono" style={{ color: 'var(--text-muted)' }}>
          Plus dozens more commercial brands scaled across Ottawa and beyond.
        </p>
      </div>
    </section>
  );
};

// ============================================
// REEL 03 - THE PROBLEM
// ============================================

const Reel03Problem = () => {
  return (
    <section id="reel-03" data-reel="03" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)` }}>
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 03 · THE REALITY CHECK
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            90% of business content gets swiped past in{' '}
            <span style={{ color: 'var(--accent-coral)' }}>1.2 seconds.</span>
          </h2>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            Posting random videos without a conversion system is a waste of time. Vanity metrics look pretty on a report, but <strong style={{ color: 'var(--accent-volt)' }}>cash collected</strong> is what scales your company. People buy from brands that grab their attention immediately and make taking action effortless. That is why we start with strategy and market math—then back it up with cinema-grade production, paid ad funnels, and high-speed websites.
          </p>

          <div className="flex flex-wrap gap-3 pt-4">
            <FloatingSticker rotation={-3}>[ZERO VANITY FLUFF]</FloatingSticker>
            <FloatingSticker rotation={2}>[BUILT FOR CPA & ROAS]</FloatingSticker>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// REEL 04 - WHAT WE DO
// ============================================

const Reel04WhatWeDo = ({ scrollToReel }) => {
  const [activeTab, setActiveTab] = useState('monthly');

  const capabilities = [
    { num: '01', title: 'Market & Offer Strategy', desc: 'We study your business, dissect your competitors, and map out the entire growth plan before a single camera rolls.' },
    { num: '02', title: 'Paid Ad Funnels (Meta, TikTok & Google)', desc: 'Hyper-targeted ad campaigns engineered to put your offer in front of high-intent buyers and bring in daily leads.' },
    { num: '03', title: 'Full Social Media Management', desc: 'Scheduling, SEO tag architecture, captions, and community management handled end-to-end.' },
    { num: '04', title: 'High-Hook Short-Form Video', desc: 'Reels and TikToks scripted, directed, and edited to lock attention from the very first frame.' },
    { num: '05', title: '4K Cinema Brand Commercials', desc: 'Studio-grade hero films (24mm · LOG · 4K · 24FPS) that make your business look untouchable in your industry.' },
    { num: '06', title: 'Conversion Websites & Landing Pages', desc: 'Sub-second, mobile-first custom websites engineered to turn cold clicks into booked calls and sales.' },
    { num: '07', title: 'Brand Identity & Launch Systems', desc: 'Logos, corporate rebrands, and day-one launch marketing for new ventures and established companies alike.' },
  ];

  const monthlyTiers = [
    { name: 'THE KICKSTART', popular: false, features: ['16 edited short-form vertical videos', 'Publishing & scheduling across 2 networks', 'Paid boost setup on 2 organic posts'] },
    { name: 'THE GROWTH', popular: true, features: ['20 edited short-form vertical videos (18 cultural/meme + 2 promotional)', 'Publishing + SEO + tag architecture across 3 networks', 'Meta or TikTok lead-conversion campaign w/ 2 A/B variants + monthly reporting'] },
    { name: 'MARKET DOMINATOR', popular: false, features: ['30 polished short-form vertical assets PLUS two (2) premium cinematic "Hero" commercial productions', 'Full management across 4 networks w/ community moderation', 'Full-funnel ads (Meta + TikTok/Google), weekly optimization, retargeting, dashboard access'] },
  ];

  const oneTimeServices = [
    { name: 'Website Design', price: '$2,500 CAD + 13% HST', features: ['Custom designed, mobile-responsive website', 'Built for conversion and brand consistency', 'Delivered ready to launch'] },
    { name: 'Logo Design', price: '$500 CAD + 13% HST', features: ['Professional brand identity', 'Multiple concepts and revisions', 'All file formats for print and digital'] },
    { name: 'Google Ads Campaign', price: '$3,500 CAD + 13% HST', features: ['Strategic keyword research & competitive analysis', 'Custom ad copy & landing page optimization', 'Campaign setup with conversion tracking', 'First month management & optimization included', 'Detailed performance reporting'] },
  ];

  return (
    <section id="reel-04" data-reel="04" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)`, backgroundColor: 'var(--surface)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 04 · THE ARSENAL
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Everything required to own your market.
          </h2>
        </motion.div>

        {/* Capabilities Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-16">
          {capabilities.map((cap, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              whileHover={{ scale: 1.02, y: -3 }}
              className="p-4 md:p-6 rounded-lg border-2 transition-all cursor-pointer"
              style={{
                backgroundColor: 'var(--background)',
                borderColor: 'var(--border)',
              }}
            >
              <div className="text-sm font-black font-mono mb-2" style={{ color: 'var(--accent-volt)' }}>
                CUT {cap.num}
              </div>
              <h3 className="text-base md:text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {cap.title}
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {cap.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Toggle: Monthly vs One-Time */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-lg border-2 p-1" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
            <button
              onClick={() => setActiveTab('monthly')}
              className={`px-6 py-2 rounded font-semibold text-sm transition-all ${activeTab === 'monthly' ? 'shadow-brutal' : ''}`}
              style={{
                backgroundColor: activeTab === 'monthly' ? 'var(--accent-volt)' : 'transparent',
                color: activeTab === 'monthly' ? 'var(--background)' : 'var(--text-primary)',
              }}
            >
              Monthly Growth Tiers
            </button>
            <button
              onClick={() => setActiveTab('onetime')}
              className={`px-6 py-2 rounded font-semibold text-sm transition-all ${activeTab === 'onetime' ? 'shadow-brutal' : ''}`}
              style={{
                backgroundColor: activeTab === 'onetime' ? 'var(--accent-volt)' : 'transparent',
                color: activeTab === 'onetime' ? 'var(--background)' : 'var(--text-primary)',
              }}
            >
              One-Time Builds
            </button>
          </div>
        </div>

        {/* Monthly Tiers */}
        {activeTab === 'monthly' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {monthlyTiers.map((tier, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`relative p-6 rounded-lg border-2 ${tier.popular ? 'shadow-brutal' : ''}`}
                style={{
                  backgroundColor: 'var(--background)',
                  borderColor: tier.popular ? 'var(--accent-volt)' : 'var(--border)',
                }}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'var(--accent-coral)', color: 'var(--background)' }}>
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-lg font-bold mb-3 uppercase" style={{ color: 'var(--text-primary)' }}>
                  {tier.name}
                </h3>
                <p className="text-xl font-black mb-4" style={{ color: 'var(--text-primary)' }}>
                  Contact Us
                </p>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((f, i) => (
                    <li key={i} className="text-xs flex items-start gap-2" style={{ color: 'var(--text-muted)' }}>
                      <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--accent-volt)' }}></span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => scrollToReel('10')}
                  className="w-full py-2.5 font-semibold rounded border-2 text-sm transition-all hover:scale-105"
                  style={{
                    backgroundColor: tier.popular ? 'var(--accent-volt)' : 'transparent',
                    borderColor: 'var(--border)',
                    color: tier.popular ? 'var(--background)' : 'var(--text-primary)',
                  }}
                >
                  Get Started →
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* One-Time Services */}
        {activeTab === 'onetime' && (
          <div>
            <p className="text-center text-sm mb-8 font-mono" style={{ color: 'var(--text-muted)' }}>
              No Retainer Required — Pay once, own it forever.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {oneTimeServices.map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-lg border-2"
                  style={{
                    backgroundColor: 'var(--background)',
                    borderColor: 'var(--border)',
                  }}
                >
                  <h4 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {service.name}
                  </h4>
                  <p className="text-xl font-black font-mono mb-4" style={{ color: 'var(--accent-volt)' }}>
                    {service.price}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((f, i) => (
                      <li key={i} className="text-xs flex items-start gap-2" style={{ color: 'var(--text-muted)' }}>
                        <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--accent-volt)' }}></span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => scrollToReel('10')}
                    className="w-full py-2.5 font-semibold rounded border-2 text-sm transition-all hover:scale-105"
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: 'var(--border)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Get Started →
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};


// ============================================
// REEL 05 - CASE STUDIES
// ============================================

const Reel05CaseStudies = () => {
  const cases = [
    {
      num: '01/05',
      client: 'Talia Flowers',
      counter: 839,
      label: 'INBOUND LEADS',
      views: '+39,748%',
      title: 'From local florist to 839 tracked customer leads.',
      story: 'We took over their short-form creative and deployed a paid acquisition engine around their arrangements—driving a 39,748% explosion in views and 839 direct leads.',
      tags: ['SHORT-FORM ENGINE', 'PAID ACQUISITION'],
    },
    {
      num: '02/05',
      client: 'Premium AutoWorks & Aqua Detailing',
      counter: 966,
      label: 'AUTOMOTIVE LEADS',
      title: 'Dominating local automotive bookings across Ottawa.',
      story: 'By pairing custom web redesigns with high-hook shop reels and local ad funnels, we generated 528 leads (+2,648% views) for Premium AutoWorks and 438 local leads (+1,948% views) for Aqua Detailing.',
      tags: ['WEB REDESIGN', 'LOCAL AD FUNNELS'],
    },
    {
      num: '03/05',
      client: 'OnPoint Towing & CarPro Auto Brokers',
      counter: 470,
      label: 'COMMERCIAL LEADS & DEALS',
      title: 'High-intent deal flow for dispatch and auto brokerage.',
      story: 'We engineered conversion web infrastructure and paid funnels that delivered 372 commercial leads for OnPoint Towing and 98 qualified vehicle deals (+8,926% views) for CarPro Auto Brokers.',
      tags: ['CONVERSION WEB', 'PAID FUNNELS'],
    },
    {
      num: '04/05',
      client: 'E.D. Waste, Cornerstone Waste & LHGS Contracting',
      counter: 1848,
      suffix: '%',
      label: 'VIEWS & FULL B2B REBRANDS',
      title: 'Enterprise infrastructure for heavy commercial & contracting brands.',
      story: 'We delivered 14 enterprise B2B leads (+1,848% views) for Cornerstone Waste Solutions, a complete corporate brand and web overhaul for E.D. Waste Management, and custom enterprise UI/UX architecture for LHGS Contracting.',
      tags: ['CORPORATE REBRAND', 'ENTERPRISE UI/UX'],
    },
    {
      num: '05/05',
      client: "Anni's Café, AnyDrop & South Keys Health Center",
      counter: 838,
      suffix: '%',
      label: 'MULTI-INDUSTRY GROWTH',
      title: 'Hospitality foot traffic, tech conversions, and medical media.',
      story: "We boosted local foot traffic and views by +638% for Anni's Café, generated 29 conversion leads (+838% views) for AnyDrop, and produced private institutional medical media for South Keys Health Center.",
      tags: ['ORGANIC VIRAL', 'INSTITUTIONAL MEDIA'],
    },
  ];

  return (
    <section id="reel-05" data-reel="05" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)` }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 05 · VERIFIED RESULTS
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Real brands. Real pipeline.
          </h2>
        </motion.div>

        {/* Mobile: Snap-Scroll */}
        <div className="md:hidden overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scroll-smooth">
          <div className="flex gap-6" style={{ width: `${cases.length * 360}px` }}>
            {cases.map((c, idx) => (
              <div key={idx} className="snap-start w-[350px]">
                <CaseCard caseData={c} index={idx} />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: Vertical Stack */}
        <div className="hidden md:block space-y-6">
          {cases.map((c, idx) => (
            <CaseCard key={idx} caseData={c} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

const CaseCard = ({ caseData, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.01, y: -3 }}
      className="p-6 md:p-8 rounded-lg border-2 transition-all"
      style={{
        backgroundColor: 'var(--surface)',
        borderColor: 'var(--border)',
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
          CASE {caseData.num} · {caseData.client.toUpperCase()}
        </span>
        {caseData.views && (
          <span className="text-xs font-mono" style={{ color: 'var(--accent-coral)' }}>
            {caseData.views} VIEW LIFT
          </span>
        )}
      </div>

      <div className="mb-6">
        <p className="text-4xl md:text-5xl font-black font-mono mb-2" style={{ color: 'var(--accent-volt)' }}>
          <CounterStat value={caseData.counter} suffix={caseData.suffix || ''} />
        </p>
        <p className="text-sm font-mono" style={{ color: 'var(--text-muted)' }}>
          {caseData.label}
        </p>
      </div>

      <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        {caseData.title}
      </h3>
      <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-muted)' }}>
        {caseData.story}
      </p>

      <div className="flex flex-wrap gap-2">
        {caseData.tags.map((tag, i) => (
          <span
            key={i}
            className="px-3 py-1 rounded-full text-xs font-mono border"
            style={{
              backgroundColor: 'var(--accent-volt)',
              borderColor: 'var(--border)',
              color: 'var(--background)',
              opacity: 0.2,
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

// ============================================
// REEL 06-10 COMPONENTS
// ============================================

const Reel06Industries = () => {
  const industries = [
    { name: 'Automotive', desc: 'Tire shops, detailing, brokers, towing' },
    { name: 'Home Services', desc: 'Waste management, contracting, HVAC' },
    { name: 'Retail & Hospitality', desc: 'Cafés, flower shops, local retail' },
    { name: 'Professional Services', desc: 'Barbershops, salons, healthcare' },
    { name: 'B2B & Enterprise', desc: 'Corporate rebrands, institutional media' },
  ];

  return (
    <section id="reel-06" data-reel="06" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)`, backgroundColor: 'var(--surface)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 06 · BUILT FOR LEADERS
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Built for businesses that refuse to blend in.
          </h2>
          <p className="text-base md:text-lg max-w-3xl" style={{ color: 'var(--text-muted)' }}>
            Automotive shops and dealerships, commercial contractors, barbershops and aesthetics, restaurants and cafés, healthcare clinics, and ambitious service brands. Different industries, same ruthless formula: strategy first, backed by cinema-grade content, conversion websites, and paid ads that scale.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {industries.map((industry, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-lg border-2 transition-all"
              style={{
                backgroundColor: 'var(--background)',
                borderColor: 'var(--border)',
              }}
            >
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {industry.name}
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {industry.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Reel07Process = () => {
  const steps = [
    { num: '01', title: '60-Second Form', desc: 'Tell us about your business and goals so we show up to our call with real insights.' },
    { num: '02', title: 'Strategy Call', desc: 'We unpack your offer, your ideal customer, and the fastest path to scaling your revenue.' },
    { num: '03', title: 'Market Research & Creative Blueprint', desc: 'We study your competitors, script the hooks, and present the complete campaign plan before filming a single frame.' },
    { num: '04', title: 'Shoot Day', desc: 'Our production crew comes to your location and captures an entire month of cinema-grade content (24mm · LOG · 4K · 24FPS) in one efficient session.' },
    { num: '05', title: 'Edit, Build & Launch', desc: 'You get razor-sharp edits ready to post, alongside paid ad funnels and web landing pages engineered to convert.' },
    { num: '06', title: 'Track, Refine & Scale', desc: 'Every month we review the numbers, cut what doesn\'t convert, and double down on the winning hooks and ads.' },
  ];

  return (
    <section id="reel-07" data-reel="07" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)` }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 07 · THE PROCESS
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            From first call to full pipeline in 6 steps.
          </h2>
        </motion.div>

        <div className="space-y-4">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="p-6 rounded-lg border-2 transition-all hover:scale-[1.01]"
              style={{
                backgroundColor: 'var(--surface)',
                borderColor: 'var(--border)',
              }}
            >
              <div className="flex gap-4">
                <span className="text-3xl font-black font-mono" style={{ color: 'var(--accent-volt)' }}>
                  {step.num}
                </span>
                <div>
                  <h3 className="text-lg md:text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {step.title}
                  </h3>
                  <p className="text-base" style={{ color: 'var(--text-muted)' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Reel08WhyIOH = () => {
  const stats = [
    { value: '680000', suffix: '%+', label: 'Total Audience Reach Expanded' },
    { value: '1980', suffix: '+', label: 'Total Conversions Engineered' },
    { value: '839', suffix: '+', label: 'Inbound Leads in a Single Campaign' },
    { value: '0', suffix: '', label: 'Locked Long-Term Traps', special: true },
    { value: '100', suffix: '%', label: 'In-House Strategy, Film, Ads & Web' },
  ];

  return (
    <section id="reel-08" data-reel="08" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)`, backgroundColor: 'var(--surface)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12 text-center">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 08 · THE SCOREBOARD
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Why brands choose Impact Over Hype.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="p-8 rounded-lg border-2 text-center"
              style={{
                backgroundColor: 'var(--background)',
                borderColor: 'var(--border)',
              }}
            >
              <p className="text-4xl md:text-5xl font-black font-mono mb-3" style={{ color: 'var(--accent-volt)' }}>
                {stat.special ? stat.value : <CounterStat value={stat.value} suffix={stat.suffix} />}
              </p>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stat.label}
              </p>
            </motion.div>
          ))}

          {/* Special Cinema Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="p-8 rounded-lg border-2 text-center flex items-center justify-center"
            style={{
              backgroundColor: 'var(--background)',
              borderColor: 'var(--accent-volt)',
            }}
          >
            <p className="text-lg font-black font-mono" style={{ color: 'var(--accent-volt)' }}>
              24mm · LOG · 4K · 24FPS<br />Cinema Production Quality
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Reel09FAQ = () => {
  const [expanded, setExpanded] = useState(null);

  const faqs = [
    {
      q: 'Do I have to sign a long, restrictive contract?',
      a: 'No. Our growth tiers run month-to-month because we earn retention through results, and our one-time builds (Websites, Logos, Google Ads setups) require zero retainer.',
    },
    {
      q: 'How many videos do I get per month?',
      a: 'Depending on your tier, you get between 16 and 30+ custom-edited vertical videos every month, plus 2 cinematic Hero commercials in our Market Dominator package.',
    },
    {
      q: 'Where do you work?',
      a: 'We are based in Ottawa, Ontario, shooting on-site across the region and running paid acquisition and web infrastructure for brands everywhere.',
    },
    {
      q: 'Can you run my Meta, TikTok, and Google ads too?',
      a: 'Yes. We run full-funnel paid campaigns on Meta, TikTok, and Google Search right alongside your organic content so views turn into tracked leads.',
    },
    {
      q: 'Can you build my website or launch a brand from scratch?',
      a: 'Yes. We design logos, build sub-second conversion websites, and launch new brands and corporate rebrands from day one.',
    },
  ];

  return (
    <section id="reel-09" data-reel="09" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)` }}>
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 09 · Q&A
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Straight answers. Zero fluff.
          </h2>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="rounded-lg border-2 overflow-hidden"
              style={{
                backgroundColor: 'var(--surface)',
                borderColor: 'var(--border)',
              }}
            >
              <button
                onClick={() => setExpanded(expanded === idx ? null : idx)}
                className="w-full text-left px-6 py-4 flex items-center justify-between hover:opacity-80 transition-opacity"
              >
                <span className="text-base font-semibold pr-4" style={{ color: 'var(--text-primary)' }}>
                  {faq.q}
                </span>
                <ChevronDown
                  size={20}
                  style={{ color: 'var(--text-muted)' }}
                  className={`transform transition-transform flex-shrink-0 ${expanded === idx ? 'rotate-180' : ''}`}
                />
              </button>
              <AnimatePresence>
                {expanded === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Reel10BookingFunnel = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    business: '',
    goal: '',
    budget: '',
    message: '',
  });

  const goalOptions = [
    'Scale Commercial Leads',
    'Viral Organic Attention',
    'Full Brand & Web Rebrand',
    'One-Time Build (Web / Ads / Logo)',
  ];

  const budgetOptions = [
    'Under $2,000',
    '$2,000 to $5,000',
    '$5,000 to $10,000',
    '$10,000+',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePillSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Strategy Call - IOH Media');
    const body = encodeURIComponent(
      `Name: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nBusiness: ${formData.business}\nGoal: ${formData.goal}\nBudget: ${formData.budget}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:ImpactOverHype@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <section id="reel-10" data-reel="10" className="py-16 md:py-24 px-4 md:px-6" style={{ borderTop: `2px solid var(--border)`, backgroundColor: 'var(--surface)' }}>
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12 text-center">
          <div className="text-xs md:text-sm font-mono mb-3" style={{ color: 'var(--text-muted)' }}>
            REEL 10 · LOCK IT IN
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Ready to turn attention into measurable pipeline?
          </h2>
          <p className="text-base" style={{ color: 'var(--text-muted)' }}>
            Tell us about your business and pick your strategy call time. Takes about 60 seconds.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-6 md:p-8 rounded-lg border-2 space-y-6"
          style={{
            backgroundColor: 'var(--background)',
            borderColor: 'var(--border)',
          }}
        >
          {/* Name Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded border-2 focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded border-2 focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded border-2 focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded border-2 focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </div>

          {/* Business */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Business Name / Website URL
            </label>
            <input
              type="text"
              name="business"
              value={formData.business}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded border-2 focus:outline-none focus:ring-2 focus:ring-offset-0"
              style={{
                backgroundColor: 'var(--surface)',
                borderColor: 'var(--border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Goal Pills */}
          <div>
            <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
              What do you want to grow? *
            </label>
            <div className="flex flex-wrap gap-2">
              {goalOptions.map((goal) => (
                <button
                  key={goal}
                  type="button"
                  onClick={() => handlePillSelect('goal', goal)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                    formData.goal === goal ? 'shadow-brutal' : ''
                  }`}
                  style={{
                    backgroundColor: formData.goal === goal ? 'var(--accent-volt)' : 'var(--surface)',
                    borderColor: 'var(--border)',
                    color: formData.goal === goal ? 'var(--background)' : 'var(--text-primary)',
                  }}
                >
                  {goal}
                </button>
              ))}
            </div>
          </div>

          {/* Budget Pills */}
          <div>
            <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
              What is your monthly marketing budget? *
            </label>
            <div className="flex flex-wrap gap-2">
              {budgetOptions.map((budget) => (
                <button
                  key={budget}
                  type="button"
                  onClick={() => handlePillSelect('budget', budget)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                    formData.budget === budget ? 'shadow-brutal' : ''
                  }`}
                  style={{
                    backgroundColor: formData.budget === budget ? 'var(--accent-volt)' : 'var(--surface)',
                    borderColor: 'var(--border)',
                    color: formData.budget === budget ? 'var(--background)' : 'var(--text-primary)',
                  }}
                >
                  {budget}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Tell us what you do and what you want to achieve (optional)
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={5}
              className="w-full px-4 py-3 rounded border-2 focus:outline-none focus:ring-2 focus:ring-offset-0"
              style={{
                backgroundColor: 'var(--surface)',
                borderColor: 'var(--border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-4 font-bold text-base rounded border-2 shadow-brutal-hover transition-all flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'var(--accent-volt)',
              borderColor: 'var(--border)',
              color: 'var(--background)',
            }}
          >
            Book My Free Strategy Call
            <ArrowRight size={20} />
          </button>

          <p className="text-xs text-center font-mono" style={{ color: 'var(--text-muted)' }}>
            No spam. We only contact you about your strategy call.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

// BOTTOM TIMELINE SCRUBBER
const BottomTimeline = ({ activeReel, scrollToReel }) => {
  const reels = ['01', '01A', '02', '03', '04', '05', '06', '07', '08', '09', '10'];

  return (
    <div
      className="hidden lg:block fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl py-3"
      style={{
        backgroundColor: 'var(--background)',
        borderTop: `2px solid var(--border)`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-center gap-1 font-mono text-xs">
          <span style={{ color: 'var(--text-muted)' }}>00:00</span>
          <span style={{ color: 'var(--text-muted)' }} className="mx-2">[</span>
          {reels.map((reel) => (
            <button
              key={reel}
              onClick={() => scrollToReel(reel)}
              className={`px-3 py-1 transition-all ${
                activeReel === reel ? 'font-bold' : ''
              }`}
              style={{
                color: activeReel === reel ? 'var(--accent-volt)' : 'var(--text-muted)',
              }}
            >
              {reel}
            </button>
          ))}
          <span style={{ color: 'var(--text-muted)' }} className="mx-2">]</span>
          <span style={{ color: 'var(--text-muted)' }}>02:30</span>
        </div>
      </div>
    </div>
  );
};

// MOBILE STICKY CTA
const MobileStickyCTA = ({ show, scrollToReel }) => {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 p-4 backdrop-blur-xl"
          style={{
            backgroundColor: 'var(--background)',
            borderTop: `2px solid var(--border)`,
          }}
        >
          <button
            onClick={() => scrollToReel('10')}
            className="w-full py-3 font-bold rounded border-2 shadow-brutal flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'var(--accent-volt)',
              borderColor: 'var(--border)',
              color: 'var(--background)',
            }}
          >
            Free Strategy Call (60s) — Book Now
            <ArrowRight size={18} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// FOOTER
const Footer = () => {
  return (
    <footer className="py-12 px-4 text-center text-sm font-mono" style={{ color: 'var(--text-muted)', borderTop: `2px solid var(--border)` }}>
      <p>IOH Media • Ottawa, Ontario • ImpactOverHype@gmail.com</p>
      <p className="mt-2">© 2026 IOH Media • Impact Over Hype</p>
    </footer>
  );
};

export default LandingPage;